/**
 * SQL Context-Aware Completion Sorter
 *
 * Uses token-based context detection to determine which keywords should be
 * prioritized based on SQL grammar. This is more robust than regex patterns.
 */

// SQL keywords that define statement structure
const SQL_KEYWORDS = new Set([
  'SELECT', 'FROM', 'WHERE', 'JOIN', 'INNER', 'LEFT', 'RIGHT', 'FULL', 'CROSS', 'OUTER',
  'ON', 'AND', 'OR', 'ORDER', 'BY', 'GROUP', 'HAVING', 'UNION', 'ALL', 'DISTINCT',
  'INSERT', 'INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE', 'CREATE', 'ALTER', 'DROP',
  'TABLE', 'VIEW', 'INDEX', 'PROCEDURE', 'FUNCTION', 'TRIGGER', 'DATABASE', 'SCHEMA',
  'AS', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'WITH', 'TOP', 'LIKE', 'IN', 'BETWEEN',
  'IS', 'NULL', 'NOT', 'EXISTS', 'ASC', 'DESC', 'LIMIT', 'OFFSET', 'FETCH', 'NEXT',
  'ROWS', 'ONLY', 'FIRST', 'ADD', 'CONSTRAINT', 'PRIMARY', 'FOREIGN', 'KEY', 'REFERENCES',
  'DEFAULT', 'CHECK', 'UNIQUE', 'EXEC', 'EXECUTE', 'DECLARE', 'BEGIN', 'COMMIT',
  'ROLLBACK', 'TRANSACTION', 'GO', 'USE', 'PRINT', 'RETURN', 'IF', 'WHILE', 'BREAK',
  'CONTINUE', 'TRY', 'CATCH', 'THROW', 'RAISERROR'
]);

// Maps the last significant keyword to what should come next
const KEYWORD_SUCCESSORS: Record<string, string[]> = {
  // SELECT clause
  'SELECT': ['TOP', 'DISTINCT', 'ALL', '*'],

  // FROM clause - these keywords typically follow FROM + table
  'FROM': ['WHERE', 'INNER', 'LEFT', 'RIGHT', 'FULL', 'CROSS', 'JOIN', 'ORDER', 'GROUP', 'HAVING', 'UNION', 'ON'],

  // JOIN keywords
  'JOIN': ['ON'],
  'INNER': ['JOIN'],
  'LEFT': ['JOIN', 'OUTER'],
  'RIGHT': ['JOIN', 'OUTER'],
  'FULL': ['JOIN', 'OUTER'],
  'CROSS': ['JOIN'],
  'OUTER': ['JOIN'],
  'ON': ['AND', 'OR', 'WHERE', 'JOIN', 'INNER', 'LEFT', 'RIGHT', 'ORDER', 'GROUP'],

  // WHERE clause
  'WHERE': ['AND', 'OR', 'NOT', 'EXISTS', 'ORDER', 'GROUP'],
  'AND': ['OR', 'NOT', 'ORDER', 'GROUP'],
  'OR': ['AND', 'NOT', 'ORDER', 'GROUP'],

  // ORDER/GROUP BY
  'ORDER': ['BY'],
  'GROUP': ['BY'],
  'BY': ['ASC', 'DESC', 'HAVING', 'ORDER'],
  'ASC': ['DESC', 'NULLS', ',', 'OFFSET', 'FETCH'],
  'DESC': ['ASC', 'NULLS', ',', 'OFFSET', 'FETCH'],
  'HAVING': ['ORDER'],

  // UNION
  'UNION': ['ALL', 'SELECT'],

  // INSERT
  'INSERT': ['INTO'],
  'INTO': ['VALUES', 'SELECT', '('],
  'VALUES': ['('],

  // UPDATE
  'UPDATE': ['SET'],
  'SET': ['WHERE'],

  // DELETE
  'DELETE': ['FROM'],

  // CREATE/ALTER/DROP
  'CREATE': ['TABLE', 'VIEW', 'INDEX', 'PROCEDURE', 'FUNCTION', 'TRIGGER', 'DATABASE', 'SCHEMA'],
  'ALTER': ['TABLE', 'VIEW', 'INDEX', 'PROCEDURE', 'FUNCTION', 'DATABASE', 'SCHEMA', 'ADD', 'DROP', 'COLUMN'],
  'DROP': ['TABLE', 'VIEW', 'INDEX', 'PROCEDURE', 'FUNCTION', 'TRIGGER', 'DATABASE', 'SCHEMA'],
  'TABLE': ['('],
  'ADD': ['CONSTRAINT', 'COLUMN'],

  // CASE
  'CASE': ['WHEN'],
  'WHEN': ['THEN'],
  'THEN': ['WHEN', 'ELSE', 'END'],
  'ELSE': ['END'],

  // WITH (CTE)
  'WITH': ['AS'],
  'AS': ['SELECT', '('],

  // Comparison
  'IS': ['NULL', 'NOT'],
  'NOT': ['NULL', 'IN', 'LIKE', 'BETWEEN', 'EXISTS'],
  'BETWEEN': ['AND'],
  'LIKE': ['AND', 'OR', 'ORDER', 'GROUP'],
  'IN': ['('],
};

/**
 * Tokenizes SQL text into keywords and identifiers
 */
function tokenizeSql(text: string): string[] {
  // Match words, keeping track of what they are
  const tokens: string[] = [];
  const regex = /([a-zA-Z_][\w]*)|('[^']*')|("[^"]*")|(\[[^\]]*\])|(\d+(?:\.\d+)?)|([^\s\w])/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const token = match[0];
    if (token.trim()) {
      tokens.push(token);
    }
  }

  return tokens;
}

/**
 * Finds the last significant SQL keyword in the token stream
 */
function findLastKeyword(tokens: string[]): { keyword: string; position: number } | null {
  for (let i = tokens.length - 1; i >= 0; i--) {
    const token = tokens[i].toUpperCase();
    if (SQL_KEYWORDS.has(token)) {
      return { keyword: token, position: i };
    }
  }
  return null;
}

/**
 * Determines if we're in a position where FROM should be suggested
 * (after SELECT with columns but before FROM appears)
 */
function shouldSuggestFrom(tokens: string[]): boolean {
  const hasSelect = tokens.some(t => t.toUpperCase() === 'SELECT');
  const hasFrom = tokens.some(t => t.toUpperCase() === 'FROM');

  // If we have SELECT but no FROM, and there's at least one token after SELECT
  if (hasSelect && !hasFrom) {
    const selectIndex = tokens.findIndex(t => t.toUpperCase() === 'SELECT');
    // Check if there's content after SELECT (columns, *, etc.)
    return selectIndex < tokens.length - 1;
  }

  return false;
}

// Priority prefix for sorting (lower = higher priority)
const PRIORITY_HIGH = '0';
const PRIORITY_MEDIUM = '1';
const PRIORITY_NORMAL = '2';
const PRIORITY_LOW = '3';

export type CompletionItem = {
  label: string;
  kind?: number;
  sortText?: string;
  preselect?: boolean;
  detail?: string;
  documentation?: any;
  insertText?: string;
  [key: string]: any;
};

/**
 * Analyzes the SQL text before the cursor and returns keywords that should be prioritized
 */
export function getContextualKeywords(textBeforeCursor: string): string[] {
  const tokens = tokenizeSql(textBeforeCursor);

  if (tokens.length === 0) {
    return ['SELECT', 'INSERT', 'UPDATE', 'DELETE', 'CREATE', 'ALTER', 'DROP', 'WITH', 'EXEC'];
  }

  // Special case: if we have SELECT with content but no FROM yet, suggest FROM
  if (shouldSuggestFrom(tokens)) {
    return ['FROM'];
  }

  // Find the last keyword to determine context
  const lastKeywordInfo = findLastKeyword(tokens);

  if (!lastKeywordInfo) {
    return [];
  }

  const { keyword, position } = lastKeywordInfo;

  // Check if there are non-keyword tokens after the last keyword
  // This helps distinguish between "FROM " and "FROM table "
  const tokensAfterKeyword = tokens.slice(position + 1);
  const hasContentAfterKeyword = tokensAfterKeyword.some(t => !SQL_KEYWORDS.has(t.toUpperCase()));

  // Get successors for this keyword
  const successors = KEYWORD_SUCCESSORS[keyword] || [];

  // If there's content after the keyword (like a table name), use the successors
  // If no content after keyword, we might still be completing the keyword's argument
  if (hasContentAfterKeyword || successors.length > 0) {
    return successors;
  }

  return [];
}

// Items that should be deprioritized (system variables, etc.)
const LOW_PRIORITY_PREFIXES = ['@@', '__'];

// LSP CompletionItemKind values
const COMPLETION_KIND = {
  KEYWORD: 14,
  VARIABLE: 6,
  FIELD: 5,
  TABLE: 7, // Class
  COLUMN: 10, // Property
} as const;

/**
 * Checks if an item should be deprioritized based on its label
 */
function shouldDeprioritize(label: string): boolean {
  return LOW_PRIORITY_PREFIXES.some(prefix => label.startsWith(prefix));
}

/**
 * Generates a sortText value based on context relevance
 */
function generateSortText(
  item: CompletionItem,
  prioritizedKeywords: string[],
  originalSortText?: string
): string {
  const label = item.label.toUpperCase();
  const itemLabel = item.label;

  // Deprioritize system variables (@@) and internal items (__)
  if (shouldDeprioritize(itemLabel)) {
    return `${PRIORITY_LOW}9_${itemLabel}`;
  }

  // Check if this item matches a prioritized keyword
  const priorityIndex = prioritizedKeywords.findIndex(
    kw => kw.toUpperCase() === label
  );

  if (priorityIndex !== -1) {
    // High priority for contextually expected items
    // Use the index to maintain relative order among prioritized items
    return `${PRIORITY_HIGH}${String(priorityIndex).padStart(2, '0')}_${itemLabel}`;
  }

  // If item was preselected by the server, give it medium priority
  if (item.preselect) {
    return `${PRIORITY_MEDIUM}_${originalSortText || itemLabel}`;
  }

  // Prioritize schema objects (tables, columns) over keywords in most contexts
  if (item.kind === COMPLETION_KIND.TABLE || item.kind === COMPLETION_KIND.COLUMN || item.kind === COMPLETION_KIND.FIELD) {
    return `${PRIORITY_MEDIUM}_${itemLabel}`;
  }

  // Use original sortText if provided, otherwise normal priority
  if (originalSortText) {
    return `${PRIORITY_NORMAL}_${originalSortText}`;
  }

  return `${PRIORITY_LOW}_${itemLabel}`;
}

/**
 * Creates fallback completion items for expected keywords when server returns nothing
 */
function createFallbackItems(keywords: string[]): CompletionItem[] {
  return keywords.map((kw, index) => ({
    label: kw,
    kind: COMPLETION_KIND.KEYWORD,
    sortText: `${PRIORITY_HIGH}${String(index).padStart(2, '0')}_${kw}`,
    insertText: kw,
    detail: 'SQL keyword',
  }));
}

/**
 * Sorts completion items based on SQL context
 *
 * @param items - The completion items from the language server
 * @param textBeforeCursor - The SQL text before the cursor position
 * @returns The items with updated sortText values
 */
export function sortCompletionItems(
  items: CompletionItem[],
  textBeforeCursor: string
): CompletionItem[] {
  const prioritizedKeywords = getContextualKeywords(textBeforeCursor);

  // If server returned nothing but we know what keywords should come next,
  // provide fallback suggestions
  if (items.length === 0 && prioritizedKeywords.length > 0) {
    return createFallbackItems(prioritizedKeywords);
  }

  return items.map(item => ({
    ...item,
    sortText: generateSortText(item, prioritizedKeywords, item.sortText)
  }));
}

/**
 * Gets the text content from the start of the current statement to the cursor
 * Handles multiple statements separated by semicolons
 */
export function getStatementTextBeforeCursor(
  fullText: string,
  cursorOffset: number
): string {
  const textBeforeCursor = fullText.substring(0, cursorOffset);

  // Find the start of the current statement (after the last semicolon or beginning)
  const lastSemicolon = textBeforeCursor.lastIndexOf(';');
  const statementStart = lastSemicolon === -1 ? 0 : lastSemicolon + 1;

  return textBeforeCursor.substring(statementStart);
}
